
/**
 * Précharge des images critiques pour améliorer l'expérience utilisateur
 * @param urls Tableau d'URLs d'images à précharger
 */
export const preloadCriticalImages = (urls: string[]) => {
  if (!urls || !Array.isArray(urls) || urls.length === 0) return;
  
  urls
    .filter(url => typeof url === 'string' && url.trim() !== '')
    .forEach(url => {
      try {
        const img = new Image();
        img.src = url;
        console.log(`Préchargement de l'image: ${url}`);
      } catch (error) {
        console.error(`Erreur lors du préchargement de l'image: ${url}`, error);
      }
    });
};

/**
 * Précharge des images visibles dans la fenêtre d'affichage
 * @param containerSelector Sélecteur CSS du conteneur où les images sont affichées
 * @param imageSelector Sélecteur CSS des images à précharger
 */
export const preloadVisibleImages = (containerSelector: string, imageSelector: string) => {
  try {
    if (typeof document === 'undefined') return;
    
    const container = document.querySelector(containerSelector);
    if (!container) {
      console.warn(`Conteneur non trouvé avec le sélecteur: ${containerSelector}`);
      return;
    }

    const images = container.querySelectorAll(imageSelector);
    if (!images.length) {
      console.log(`Aucune image trouvée avec le sélecteur ${imageSelector} dans le conteneur ${containerSelector}`);
      return;
    }

    const urls = Array.from(images)
      .map(img => (img as HTMLImageElement).dataset.src || (img as HTMLImageElement).src)
      .filter(url => url && typeof url === 'string' && url.trim() !== '');

    if (urls.length > 0) {
      console.log(`Préchargement de ${urls.length} images visibles`);
      preloadCriticalImages(urls);
    }
  } catch (error) {
    console.error("Erreur lors du préchargement des images visibles:", error);
  }
};

/**
 * Vérifie si une URL d'image est valide
 * @param url URL de l'image à vérifier
 * @returns true si l'URL est valide, sinon false
 */
export const isValidImageUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  
  // Vérifier si l'URL est une chaîne non vide et ne contient pas 'undefined' ou 'null'
  return typeof url === 'string' && 
    url.trim() !== '' && 
    !url.includes('undefined') && 
    !url.includes('null');
};

/**
 * Compresse une image avant l'upload pour optimiser la taille
 * @param file Fichier image à compresser
 * @param maxWidth Largeur maximale de l'image
 * @param quality Qualité de l'image (0-1)
 * @returns Promise avec le fichier compressé
 */
export const compressImageBeforeUpload = async (
  file: File, 
  maxWidth = 1200, 
  quality = 0.8
): Promise<File> => {
  if (!file) {
    console.error('Fichier manquant pour la compression');
    return Promise.reject(new Error('Aucun fichier fourni'));
  }

  return new Promise((resolve, reject) => {
    try {
      // Si ce n'est pas une image, retourner le fichier original
      if (!file.type.startsWith('image/')) {
        console.log('Fichier non compressé (pas une image):', file.name);
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event) => {
        if (!event.target?.result) {
          console.error('Échec de lecture du fichier');
          resolve(file); // Fallback au fichier original
          return;
        }

        const img = new Image();
        img.src = event.target.result as string;
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            
            // Redimensionner si nécessaire
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
            
            canvas.width = width;
            canvas.height = height;
            
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              console.warn('Canvas context non disponible, utilisation du fichier original');
              resolve(file);
              return;
            }
            
            ctx.drawImage(img, 0, 0, width, height);
            
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  console.warn('Échec de compression, utilisation du fichier original');
                  resolve(file);
                  return;
                }
                
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                
                console.log(`Image compressée: ${file.name} (${Math.round(file.size / 1024)} KB -> ${Math.round(compressedFile.size / 1024)} KB)`);
                resolve(compressedFile);
              },
              'image/jpeg',
              quality
            );
          } catch (canvasError) {
            console.error('Erreur lors du traitement du canvas:', canvasError);
            resolve(file);
          }
        };
        
        img.onerror = () => {
          console.warn('Erreur lors du chargement de l\'image, utilisation du fichier original');
          resolve(file);
        };
      };
      
      reader.onerror = () => {
        console.warn('Erreur lors de la lecture du fichier, utilisation du fichier original');
        resolve(file);
      };
    } catch (error) {
      console.error('Erreur lors de la compression de l\'image:', error);
      resolve(file); // En cas d'erreur, on retourne le fichier original
    }
  });
};

/**
 * Vérifie si une image est accessible et valide
 * @param url URL de l'image à vérifier
 * @returns Promise<boolean>
 */
export const checkImageAvailability = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!isValidImageUrl(url)) {
      console.log(`URL invalide: ${url}`);
      resolve(false);
      return;
    }

    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => {
      console.warn(`Image non disponible: ${url}`);
      resolve(false);
    };
    img.src = url;
  });
};
